import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { musinsaOrderClient } from '../musinsaOrderClient';

const GetMemberAddressRequestSchema = z
  .object({
    /** 무신사 배송지 ID (order-group 팝업이 postMessage 로 전달한 selectedAddressId). */
    addressId: z.number().int(),
  })
  .describe('GetMemberAddressRequest');

export type GetMemberAddressRequest = z.infer<typeof GetMemberAddressRequestSchema>;

/**
 * 무신사 order-service 배송지 단건 응답 data.
 * (불필요 필드는 zod 가 strip — 매핑에 필요한 키만 정의)
 */
const MemberAddressSchema = z
  .object({
    id: z.number().int(),
    /** 배송지 별칭 (예: "배송지", "집"). */
    title: z.string().nullable().optional(),
    /** 수령인. */
    name: z.string(),
    phone: z.string().nullable().optional(),
    /** 휴대폰 (배송 연락처 — phone 보다 우선). */
    mobile: z.string().nullable().optional(),
    zipcode: z.string(),
    address1: z.string(),
    address2: z.string().nullable().optional(),
    /** 배송 메모. */
    deliveryMessage: z.string().nullable().optional(),
    isDefault: z.boolean(),
  })
  .describe('MemberAddress');

export type MemberAddress = z.infer<typeof MemberAddressSchema>;

/** order-service 응답 envelope — curator 표준 래퍼가 아닌 `{ result, message, data }`. */
const GetMemberAddressResponseSchema = z
  .object({
    result: z.boolean(),
    message: z.string(),
    data: MemberAddressSchema,
  })
  .describe('GetMemberAddressResponse');

export type GetMemberAddressResponse = z.infer<typeof GetMemberAddressResponseSchema>;

/**
 * 무신사 배송지 단건 조회.
 * `GET {MUSINSA_BASE_URL}/order-service/my/addresses/getMemberAddress/{addressId}`
 * `.musinsa.com` 쿠키 인증(withCredentials) — 무신사 환경 전용(호출자 service 가드).
 */
export const getMemberAddress = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetMemberAddressRequestSchema),
    async ({ addressId }: GetMemberAddressRequest): Promise<GetMemberAddressResponse> =>
      executeStandardRequest(async () =>
        musinsaOrderClient.get<GetMemberAddressResponse>(
          urlJoin('/order-service/my/addresses/getMemberAddress', String(addressId)),
        ),
      ),
    validateApiResponse(GetMemberAddressResponseSchema),
  ),
);
